import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import maplibregl, { Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-geocoder/dist/maplibre-gl-geocoder.css";
import MaplibreGeocoder from "@maplibre/maplibre-gl-geocoder";
import * as turf from "@turf/turf";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  styled,
  TextField,
  Tooltip,
  Typography,
  Slider,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MapIcon from "@mui/icons-material/Map";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import {
  ActionsPicker,
  guidGenerator,
  useDebounce,
  useViewport,
} from "../../helpers";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { myExpariencesService } from "../../services";
import PurchaseGeolocMapList from "./PurchaseGeolocMapList";
import GeolocMapList from "../geoloc/GeolocMapList";

const PREFIX = "PurchaseGeolocMap";

const classes = {
  root: `${PREFIX}-root`,
  mapContainer: `${PREFIX}-mapContainer`,
  shoppingCartContainer: `${PREFIX}-shoppingCartContainer`,
  shoppingCart: `${PREFIX}-shoppingCart`,
  shoppingCartTitleContainer: `${PREFIX}-shoppingCartTitleContainer`,
  shoppingCartTitle: `${PREFIX}-shoppingCartTitle`,
  positionCardContainer: `${PREFIX}-positionCardContainer`,
  positionCard: `${PREFIX}-positionCard`,
  positionCardInfos: `${PREFIX}-positionCardInfos`,
  positionCardInfosInput: `${PREFIX}-positionCardInfosInput`,
  positionCardActions: `${PREFIX}-positionCardActions`,
  shoppingCartPayment: `${PREFIX}-shoppingCartPayment`,
  shoppingCartPaymentEmpty: `${PREFIX}-shoppingCartPaymentEmpty`,
  shoppingCartPaymentButton: `${PREFIX}-shoppingCartPaymentButton`,
};

const PurchaseGeolocMapContainer = styled("div")(({ theme }) => ({
  width: "100%",
  [`& .${classes.root}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "20px 0 0 0",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      margin: "0 0 10px 0",
    },
  },

  [`& .${classes.mapContainer}`]: {
    height: "450px",
    [theme.breakpoints.down("md")]: {
      height: "300px",
    },
  },

  [`& .${classes.shoppingCart}`]: {
    width: "100%",
    paddingTop: "10px",
    marginBottom: "10px",
  },

  [`& .${classes.positionCardContainer}`]: {
    maxHeight: "750px",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.positionCard}`]: {
    margin: "10px 20px",
    overflow: "hidden",
    padding: "10px 25px",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      margin: "10px 0",
      padding: "10px 15px",
    },
  },

  [`& .${classes.positionCardInfos}`]: {
    width: "60%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  [`& .${classes.positionCardInfosInput}`]: {
    marginBottom: "20px",
  },

  [`& .${classes.positionCardActions}`]: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    position: "relative",
    top: "25px",
  },

  [`& .${classes.shoppingCartTitleContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  [`& .${classes.shoppingCartTitle}`]: {
    margin: "10px 20px 10px 0",
  },

  [`& .${classes.shoppingCartPayment}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "absolute",
    left: "18%",
    marginTop: "20px",
    [theme.breakpoints.down("md")]: {
      left: "5%",
    },
  },

  [`& .${classes.shoppingCartPaymentEmpty}`]: {
    position: "static",
    margin: "1rem 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  [`& .${classes.shoppingCartPaymentButton}`]: {
    position: "sticky",
    bottom: "20px",
    right: "30%",
    zIndex: 1000,
    width: "100%",
    marginTop: "20px",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      right: 0,
      bottom: 0,
      borderRadius: 0,
    },
  },
}));

const Geo = {
  forwardGeocode: async (config) => {
    const features = [];
    try {
      let request =
        "https://nominatim.openstreetmap.org/search?q=" +
        config.query +
        "&format=geojson&polygon_geojson=1&addressdetails=1";
      const response = await fetch(request);
      const geojson = await response.json();
      for (let feature of geojson.features) {
        let center = [
          feature.bbox[0] + (feature.bbox[2] - feature.bbox[0]) / 2,
          feature.bbox[1] + (feature.bbox[3] - feature.bbox[1]) / 2,
        ];
        let point = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: center,
          },
          place_name: feature.properties.display_name,
          properties: feature.properties,
          text: feature.properties.display_name,
          place_type: ["place"],
          center: center,
        };
        features.push(point);
      }
    } catch (e) {
      console.error(`Failed to forwardGeocode with error: ${e}`);
    }

    return {
      features: features,
    };
  },
};

const geocoder = new MaplibreGeocoder(Geo, {
  maplibregl,
  showResultsWhileTyping: true,
  marker: false,
});

const StyledGeocoderPaper = styled(Paper)(({ theme }) => ({
  "& .maplibregl-ctrl-geocoder--input": {
    backgroundColor: "#121212",
    color: "#d3af5f",
  },

  "& .maplibregl-ctrl-geocoder--input::placeholder": {
    color: "#e5c08c",
  },

  "& .maplibregl-ctrl-geocoder--input::-webkit-input-placeholder": {
    color: "#e5c08c",
  },

  "& .maplibregl-ctrl-geocoder--input::-moz-placeholder": {
    color: "#e5c08c",
  },

  "& .maplibregl-ctrl-geocoder--input:-ms-input-placeholder": {
    color: "#e5c08c",
  },

  "& .maplibregl-ctrl-geocoder--icon-search": {
    fill: "#d3af5f",
  },

  "& .maplibregl-ctrl-geocoder--button": {
    backgroundColor: "#121212",
  },

  "& .maplibregl-ctrl-geocoder--icon-close": {
    right: "12px",
    top: "13px",
    fill: "#d3af5f",
  },

  "& .maplibregl-ctrl-geocoder .suggestions": {
    background: "#121212",
  },

  "& .maplibregl-ctrl-geocoder .suggestions li:not(:last-child)": {
    borderBottom: "1px solid white",
  },

  "& .maplibregl-ctrl-geocoder--result": {
    color: "#d3af5f",
  },

  "& .maplibregl-ctrl-geocoder--result-title": {
    fontWeight: "500",
  },

  "& .maplibregl-ctrl-geocoder--result-icon path": {
    fill: "#d3af5f",
  },

  "& .maplibregl-ctrl-geocoder--input:focus": {
    color: "#d3af5f",
  },
}));

const DEFAULT_RADIUS = 500;
const DEFAULT_NUMBER_OF_DAYS = 7;

// Function to create a unified geolocation object
const createGeolocItem = (options = {}, priceGeolocValue = 0) => {
  const {
    id = guidGenerator(),
    positionName = "",
    latitude = "",
    longitude = "",
    radius = DEFAULT_RADIUS,
    price = priceGeolocValue,
    startDate = dayjs().format().substring(0, 10),
    numberOfDays = DEFAULT_NUMBER_OF_DAYS,
  } = options;

  // Format for shoppingCart
  const cartItem = {
    id,
    positionName,
    latitude,
    longitude,
    radius,
    price,
    startDate,
    numberOfDays,
  };

  // Format for shoppingCartInputs
  const inputItem = {
    positionInput: positionName,
    radiusInput: radius,
    startDateInput: startDate ? dayjs(startDate) : dayjs(),
    numberOfDaysInput: numberOfDays,
  };

  return { cartItem, inputItem };
};

const PurchaseGeolocMap = forwardRef(
  (
    {
      initialViewState,
      exparienceID,
      priceGeoloc,
      displayOnly,
      purchasedGeolocs,
      mapID,
      geolocOnly,
      geolocs,
      save,
      alreadyPurchased,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { width } = useViewport();
    const lastIndex = useRef(-1);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const mapInput = useRef(null);
    const [isSatellite, setIsSatellite] = useState(true);

    const [shoppingCartInputs, setShoppingCartInputs] = useState([]);
    const [shoppingCart, setShoppingCart] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [dialogData, setDialogData] = useState(
      createGeolocItem({}, priceGeoloc)
    );
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);

    const defaultGeoloc = useCallback((name, radius) => {
      return {
        positionInput: name,
        radiusInput: radius,
        startDateInput: dayjs(),
        numberOfDaysInput: DEFAULT_NUMBER_OF_DAYS,
      };
    }, []);

    const debouncedShoppingCart = useDebounce(shoppingCartInputs, 500);

    useEffect(() => {
      if (
        shoppingCart.length > 0 &&
        debouncedShoppingCart &&
        lastIndex.current !== -1 &&
        lastIndex.current < shoppingCart.length &&
        shoppingCartInputs.length > lastIndex.current
      ) {
        let tmp = [...shoppingCart];

        tmp[lastIndex.current] = {
          ...tmp[lastIndex.current],
          positionName:
            shoppingCartInputs[lastIndex.current].positionInput || "",
          radius:
            shoppingCartInputs[lastIndex.current].radiusInput || DEFAULT_RADIUS,
          startDate:
            shoppingCartInputs[lastIndex.current].startDateInput || dayjs(),
          numberOfDays:
            shoppingCartInputs[lastIndex.current].numberOfDaysInput ||
            DEFAULT_NUMBER_OF_DAYS,
        };
        setShoppingCart(tmp);
      }
    }, [debouncedShoppingCart, shoppingCartInputs]);

    useEffect(() => {
      const mapContainer = document.getElementById(mapID);

      const map = new maplibregl.Map({
        container: mapContainer,
        style:
          "https://api.maptiler.com/maps/hybrid/style.json?key=1xiFM2Z0YcdYA1H1lhdB",
        center: [initialViewState.longitude, initialViewState.latitude],
        zoom: 11,
        touchZoomRotate: false,
      });

      // Show geocoder when not in displayOnly mode OR when in geolocOnly mode
      if (!displayOnly || geolocOnly) {
        map.addControl(geocoder);
      }
      mapRef.current = map;

      // Ajouter le flyTo vers la première position si nécessaire
      map.on("load", () => {
        if (displayOnly && purchasedGeolocs && purchasedGeolocs.length > 0) {
          // Créer un bounds qui englobe toutes les positions
          const bounds = new maplibregl.LngLatBounds();
          purchasedGeolocs.forEach((item) => {
            if (item.position && item.position.coordinates) {
              const [longitude, latitude] = item.position.coordinates;
              bounds.extend([longitude, latitude]);
            }
          });

          // Ajuster la vue pour montrer toutes les positions
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds, {
              padding: 50,
              maxZoom: 11,
              duration: 1000,
            });
          }
        } else if (geolocOnly && geolocs && geolocs.length > 0) {
          // Créer un bounds qui englobe toutes les positions
          const bounds = new maplibregl.LngLatBounds();
          geolocs.forEach((item) => {
            if (item.lat && item.long) {
              bounds.extend([item.long, item.lat]);
            }
          });

          // Ajuster la vue pour montrer toutes les positions
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds, {
              padding: 50,
              maxZoom: 11,
              duration: 1000,
            });
          }
        } else if (!displayOnly && !geolocOnly) {
          // Essayer d'obtenir la position de l'utilisateur
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const { latitude, longitude } = position.coords;
                map.flyTo({
                  center: [longitude, latitude],
                  zoom: 12,
                  duration: 1000,
                });
              },
              (error) => {
                console.warn("Error getting user location:", error);
                // En cas d'erreur, on reste sur initialViewState
              }
            );
          }
        }
      });
    }, [
      initialViewState.latitude,
      initialViewState.longitude,
      displayOnly,
      geolocOnly,
      mapID,
      purchasedGeolocs,
      geolocs,
    ]);

    useEffect(() => {
      // Add event listener for geocoder result
      const handleGeocoderResult = (e) => {
        mapRef.current.flyTo({
          center: e.result.center,
          zoom: 12,
          speed: 1.3,
        });

        // When geolocOnly is true, add the location to geolocsList instead of shopping cart
        if (geolocOnly && geolocs) {
          const { coordinates } = e.result.geometry;
          const newLocation = {
            guid: guidGenerator(true),
            lat: coordinates[1],
            long: coordinates[0],
            title: e.result.text,
            radius: DEFAULT_RADIUS,
            visible: true,
            free: false,
            controlDate: false,
            startDate: null,
            endDate: null,
          };

          // Add to geolocs list and call save function
          const updatedGeolocs = [...geolocs, newLocation];
          if (save && typeof save === "function") {
            save(updatedGeolocs);
          }

          // Clear geocoder input after adding location
          if (mapInput.current) {
            mapInput.current.value = "";
          }
        } else {
          // Original behavior for shopping cart
          const { coordinates } = e.result.geometry;
          const { cartItem, inputItem } = createGeolocItem(
            {
              positionName: e.result.text,
              longitude: coordinates[0],
              latitude: coordinates[1],
            },
            priceGeoloc
          );

          setShoppingCart((prevCart) => [cartItem, ...prevCart]);
          setShoppingCartInputs((prevCart) => [inputItem, ...prevCart]);
        }
      };

      geocoder.on("result", handleGeocoderResult);

      // Clean up event listener on unmount
      return () => {
        geocoder.off("result", handleGeocoderResult);
      };
    }, [geolocOnly, geolocs, priceGeoloc, save]);

    useEffect(() => {
      const input = document.querySelector(".maplibregl-ctrl-geocoder--input");
      mapInput.current = input;
      if (mapInput.current) {
        // Set appropriate placeholder based on mode
        const placeholder = t("purchaseGeoloc.addAGeoloc");

        mapInput.current.placeholder = placeholder;
        mapInput.current.ariaLabel = placeholder;
      }
    }, [t, geolocOnly]);

    useEffect(() => {
      const options = { steps: 64, units: "meters" };

      const timeout = setTimeout(() => {
        if (displayOnly && purchasedGeolocs && purchasedGeolocs.length > 0) {
          // Create a bounds object to track the extent of all markers
          const bounds = new maplibregl.LngLatBounds();

          // Clean existing markers and sources first
          Object.keys(markersRef.current).forEach((id) => {
            if (markersRef.current[id]) {
              markersRef.current[id].remove();
              delete markersRef.current[id];
            }

            if (mapRef.current.getLayer(`location-radius-${id}`)) {
              mapRef.current.removeLayer(`location-radius-${id}`);
            }
            if (mapRef.current.getLayer(`location-radius-outline-${id}`)) {
              mapRef.current.removeLayer(`location-radius-outline-${id}`);
            }
            if (mapRef.current.getSource(`location-radius-${id}`)) {
              mapRef.current.removeSource(`location-radius-${id}`);
            }
          });

          purchasedGeolocs.forEach((item) => {
            if (!item || !item.position || !item.position.coordinates) {
              console.warn("Invalid item in purchasedGeolocs:", item);
              return;
            }

            const { _id, position } = item;
            // Vérifier que l'id existe
            if (!_id) {
              console.warn("Item missing _id:", item);
              return;
            }

            const radius = position.radius || DEFAULT_RADIUS;
            const [longitude, latitude] = position.coordinates;

            // Vérifier que les coordonnées sont valides
            if (typeof longitude !== "number" || typeof latitude !== "number") {
              console.warn("Invalid coordinates:", position.coordinates);
              return;
            }

            // Extend bounds to include this point
            bounds.extend([longitude, latitude]);

            const circle = turf.circle([longitude, latitude], radius, options);
            const sourceId = `location-radius-${_id}`;
            const outlineId = `location-radius-outline-${_id}`;

            try {
              // Create marker and add to map
              const marker = new Marker({ color: "#000" })
                .setLngLat([longitude, latitude])
                .addTo(mapRef.current);
              markersRef.current[_id] = marker;

              // Add GeoJSON source for circle
              if (!mapRef.current.getSource(sourceId)) {
                mapRef.current.addSource(sourceId, {
                  type: "geojson",
                  data: circle,
                });

                // Add fill layer for circle
                mapRef.current.addLayer({
                  id: sourceId,
                  type: "fill",
                  source: sourceId,
                  paint: {
                    "fill-color": "#D3AF5F",
                    "fill-opacity": 0.5,
                  },
                });

                // Add outline layer for circle
                mapRef.current.addLayer({
                  id: outlineId,
                  type: "line",
                  source: sourceId,
                  paint: {
                    "line-color": "#D3AF5F",
                    "line-width": 3,
                  },
                });
              }
            } catch (error) {
              console.error("Error adding marker or circle:", error);
            }
          });

          // After adding all markers, adjust the view to show all points
          if (!bounds.isEmpty()) {
            if (mapRef.current.loaded()) {
              mapRef.current.fitBounds(bounds, {
                padding: 50,
                maxZoom: 11,
                duration: 1000,
              });
            } else {
              mapRef.current.once("load", () => {
                mapRef.current.fitBounds(bounds, {
                  padding: 50,
                  maxZoom: 11,
                  duration: 1000,
                });
              });
            }
          }
        } else {
          // Clean existing markers and sources to avoid duplicates
          Object.keys(markersRef.current).forEach((id) => {
            if (markersRef.current[id]) {
              markersRef.current[id].remove();
              delete markersRef.current[id];
            }

            if (mapRef.current.getLayer(`location-radius-${id}`)) {
              mapRef.current.removeLayer(`location-radius-${id}`);
            }
            if (mapRef.current.getLayer(`location-radius-outline-${id}`)) {
              mapRef.current.removeLayer(`location-radius-outline-${id}`);
            }
            if (mapRef.current.getSource(`location-radius-${id}`)) {
              mapRef.current.removeSource(`location-radius-${id}`);
            }
          });

          shoppingCart.forEach((item, index) => {
            const { id, longitude, latitude } = item;
            const radius =
              shoppingCartInputs[index]?.radiusInput || DEFAULT_RADIUS;
            const circle = turf.circle([longitude, latitude], radius, options);

            const sourceId = `location-radius-${id}`;
            const outlineId = `location-radius-outline-${id}`;

            // Create marker
            const marker = new Marker({ color: "#000" })
              .setLngLat([longitude, latitude])
              .addTo(mapRef.current);
            markersRef.current[id] = marker;

            // Add GeoJSON source for circle
            mapRef.current.addSource(sourceId, {
              type: "geojson",
              data: circle,
            });

            // Add fill layer for circle
            mapRef.current.addLayer({
              id: sourceId,
              type: "fill",
              source: sourceId,
              paint: {
                "fill-color": "#D3AF5F",
                "fill-opacity": 0.5,
              },
            });

            // Add outline layer for circle
            mapRef.current.addLayer({
              id: outlineId,
              type: "line",
              source: sourceId,
              paint: {
                "line-color": "#D3AF5F",
                "line-width": 3,
              },
            });
          });
        }
      }, 500);

      return () => clearTimeout(timeout);
    }, [shoppingCart, shoppingCartInputs, isSatellite]);

    // Effect to handle markers in geolocOnly mode
    useEffect(() => {
      if (!geolocOnly || !geolocs || !mapRef.current) return;

      const options = { steps: 64, units: "meters" };

      const timeout = setTimeout(() => {
        // Clean existing markers and sources to avoid duplicates
        Object.keys(markersRef.current).forEach((id) => {
          if (markersRef.current[id]) {
            markersRef.current[id].remove();
            delete markersRef.current[id];
          }

          if (mapRef.current.getLayer(`location-radius-${id}`)) {
            mapRef.current.removeLayer(`location-radius-${id}`);
          }
          if (mapRef.current.getLayer(`location-radius-outline-${id}`)) {
            mapRef.current.removeLayer(`location-radius-outline-${id}`);
          }
          if (mapRef.current.getSource(`location-radius-${id}`)) {
            mapRef.current.removeSource(`location-radius-${id}`);
          }
        });

        // Create a bounds object to adjust the view
        const bounds = new maplibregl.LngLatBounds();

        geolocs.forEach((item) => {
          if (!item.lat || !item.long) return;

          const { guid, radius, lat, long } = item;
          const actualRadius = radius || DEFAULT_RADIUS;

          // Extend bounds
          bounds.extend([long, lat]);

          const circle = turf.circle([long, lat], actualRadius, options);
          const sourceId = `location-radius-${guid}`;
          const outlineId = `location-radius-outline-${guid}`;

          // Create marker
          const marker = new Marker({ color: "#000" })
            .setLngLat([long, lat])
            .addTo(mapRef.current);
          markersRef.current[guid] = marker;

          // Add circle
          mapRef.current.addSource(sourceId, {
            type: "geojson",
            data: circle,
          });

          // Add fill layer
          mapRef.current.addLayer({
            id: sourceId,
            type: "fill",
            source: sourceId,
            paint: {
              "fill-color": "#D3AF5F",
              "fill-opacity": 0.5,
            },
          });

          // Add outline layer
          mapRef.current.addLayer({
            id: outlineId,
            type: "line",
            source: sourceId,
            paint: {
              "line-color": "#D3AF5F",
              "line-width": 3,
            },
          });
        });

        // Adjust view if markers have been added
        if (!bounds.isEmpty()) {
          if (mapRef.current.loaded()) {
            fitBoundsToMarkers();
          } else {
            mapRef.current.once("load", fitBoundsToMarkers);
          }

          function fitBoundsToMarkers() {
            mapRef.current.fitBounds(bounds, {
              padding: 50,
              maxZoom: 11,
              duration: 1000,
            });
          }
        }
      }, 500);

      return () => clearTimeout(timeout);
    }, [geolocOnly, geolocs]);

    const handleCartInfoModification = (e, index, key) => {
      lastIndex.current = index;
      const tmp = [...shoppingCartInputs];
      tmp[index] = {
        ...tmp[index],
        [key]:
          key === "startDateInput" || key === "radiusInput"
            ? e
            : e.target.value,
      };
      setShoppingCartInputs(tmp);
    };

    const handlePositionDeletion = (id, index) => {
      setItemToDelete({ id, index });
      setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
      if (itemToDelete) {
        const { id, index } = itemToDelete;
        const tmp = [...shoppingCart];
        const tmp2 = [...shoppingCartInputs];
        tmp.splice(index, 1);
        tmp2.splice(index, 1);

        if (
          lastIndex.current === index ||
          (lastIndex.current >= tmp.length && tmp.length > 0)
        ) {
          lastIndex.current = -1;
        } else if (lastIndex.current > index) {
          lastIndex.current--;
        }

        setShoppingCart(tmp);
        setShoppingCartInputs(tmp2);

        if (markersRef.current[id]) {
          markersRef.current[id].remove();
          delete markersRef.current[id];
        }

        if (mapRef.current.getLayer(`location-radius-${id}`)) {
          mapRef.current.removeLayer(`location-radius-${id}`);
        }
        if (mapRef.current.getLayer(`location-radius-outline-${id}`)) {
          mapRef.current.removeLayer(`location-radius-outline-${id}`);
        }
        if (mapRef.current.getSource(`location-radius-${id}`)) {
          mapRef.current.removeSource(`location-radius-${id}`);
        }
      }
      setOpenDeleteDialog(false);
      setItemToDelete(null);
    };

    const handleCancelDelete = () => {
      setOpenDeleteDialog(false);
      setItemToDelete(null);
    };

    const handlePositionFocus = (index, purchasedGeolocList) => {
      let longitude, latitude;

      if (purchasedGeolocList) {
        // Case for PurchaseGeolocMapList
        longitude = purchasedGeolocList.longitude;
        latitude = purchasedGeolocList.latitude;
      } else if (geolocOnly && geolocs && geolocs[index]) {
        // Case for GeolocMapList
        longitude = geolocs[index].long;
        latitude = geolocs[index].lat;
      } else {
        // Case for shopping cart
        longitude = shoppingCart[index].longitude;
        latitude = shoppingCart[index].latitude;
      }

      mapRef.current.flyTo({ center: [longitude, latitude], zoom: 12 });
    };

    const handlePayment = async () => {
      const dataArray = [...shoppingCart];
      dataArray.forEach((item, index) => {
        const radius = shoppingCartInputs[index]?.radiusInput || DEFAULT_RADIUS;
        let dailyPrice = item.price;

        // Calcul du prix journalier en fonction du rayon
        if (radius > DEFAULT_RADIUS) {
          const additionalRadius = radius - DEFAULT_RADIUS;
          const numberOf50mIncrements = Math.floor(additionalRadius / 50);
          dailyPrice = item.price + item.price * 0.1 * numberOf50mIncrements;
        }

        item.priceTotal = dailyPrice * item.numberOfDays;
        delete item.id;
        delete item.price;
      });
      await myExpariencesService.sendPurchasedGeoloc(exparienceID, dataArray);
    };

    const totalAmount = shoppingCart.reduce(
      (total, item, index) => {
        const price = Number(item.price) || 0;
        const radius =
          Number(shoppingCartInputs[index]?.radiusInput) || DEFAULT_RADIUS;
        const numberOfDays =
          Number(item.numberOfDays) || DEFAULT_NUMBER_OF_DAYS;

        let dailyPrice = price;
        if (radius > DEFAULT_RADIUS) {
          const additionalRadius = radius - DEFAULT_RADIUS;
          const numberOf50mIncrements = Math.floor(additionalRadius / 50);
          dailyPrice = price + price * 0.1 * numberOf50mIncrements;
        }

        return total + dailyPrice * numberOfDays;
      },
      alreadyPurchased ? 0 : 300
    );

    // Expose the method flyTo for being used from the parent component
    useImperativeHandle(ref, () => ({
      flyTo: (longitude, latitude) => {
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: 12,
          });
        }
      },
    }));

    const handleZoomIn = () => {
      if (mapRef.current) {
        mapRef.current.zoomIn();
      }
    };

    const handleZoomOut = () => {
      if (mapRef.current) {
        mapRef.current.zoomOut();
      }
    };

    const handleToggleMapStyle = () => {
      if (mapRef.current) {
        const newStyle = isSatellite
          ? "https://api.maptiler.com/maps/basic-v2/style.json?key=1xiFM2Z0YcdYA1H1lhdB"
          : "https://api.maptiler.com/maps/hybrid/style.json?key=1xiFM2Z0YcdYA1H1lhdB";

        // Store current data to rebuild after style change
        const options = { steps: 64, units: "meters" };
        let itemsToRebuild = [];

        if (displayOnly && purchasedGeolocs && purchasedGeolocs.length > 0) {
          // In displayOnly mode, use purchasedGeolocs data
          purchasedGeolocs.forEach((item) => {
            const { _id, position } = item;
            const radius = position.radius || DEFAULT_RADIUS;
            const longitude = position.coordinates[0];
            const latitude = position.coordinates[1];

            itemsToRebuild.push({
              id: _id,
              radius,
              longitude,
              latitude,
            });
          });
        } else if (geolocOnly && geolocs && geolocs.length > 0) {
          // In geolocOnly mode, use geolocs data
          geolocs.forEach((item) => {
            if (!item.lat || !item.long) return;

            const { guid, radius, lat, long } = item;
            const actualRadius = radius || DEFAULT_RADIUS;

            itemsToRebuild.push({
              id: guid,
              radius: actualRadius,
              longitude: long,
              latitude: lat,
            });
          });
        } else if (shoppingCart && shoppingCart.length > 0) {
          // In shopping cart mode, use shoppingCart data
          shoppingCart.forEach((item) => {
            const { id, radius, longitude, latitude } = item;

            itemsToRebuild.push({
              id,
              radius,
              longitude,
              latitude,
            });
          });
        }

        // Change map style
        mapRef.current.setStyle(newStyle);

        // Rebuild circles when style is loaded
        mapRef.current.once("style.load", () => {
          itemsToRebuild.forEach((item) => {
            const { id, radius, longitude, latitude } = item;

            // Create circle
            const circle = turf.circle([longitude, latitude], radius, options);
            const sourceId = `location-radius-${id}`;
            const outlineId = `location-radius-outline-${id}`;

            // Add source
            mapRef.current.addSource(sourceId, {
              type: "geojson",
              data: circle,
            });

            // Add fill layer
            mapRef.current.addLayer({
              id: sourceId,
              type: "fill",
              source: sourceId,
              paint: {
                "fill-color": "#D3AF5F",
                "fill-opacity": 0.5,
              },
            });

            // Add outline layer
            mapRef.current.addLayer({
              id: outlineId,
              type: "line",
              source: sourceId,
              paint: {
                "line-color": "#D3AF5F",
                "line-width": 3,
              },
            });
          });
        });

        setIsSatellite(!isSatellite);
      }
    };

    const handleAddCurrentPosition = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = {
            id: guidGenerator(true),
            longitude: longitude,
            latitude: latitude,
            positionName: `Position (${latitude}, ${longitude})`,
            radius: DEFAULT_RADIUS,
            price: priceGeoloc,
            numberOfDays: DEFAULT_NUMBER_OF_DAYS,
            startDate: dayjs().format().substring(0, 10),
          };

          setShoppingCart((prevCart) => [newLocation, ...prevCart]);
          setShoppingCartInputs((prevCart) => [
            defaultGeoloc(newLocation.positionName, newLocation.radius),
            ...prevCart,
          ]);
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: 12,
          });
        });
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    };

    const isUserPositionInCart = () => {
      return shoppingCart.some((item) => {
        const userLat = parseFloat(item.latitude);
        const userLong = parseFloat(item.longitude);
        return (
          !isNaN(userLat) &&
          !isNaN(userLong) &&
          Math.abs(userLat - parseFloat(item.latitude)) < 0.0001 &&
          Math.abs(userLong - parseFloat(item.longitude)) < 0.0001
        );
      });
    };

    const handleOpenDialog = () => {
      setDialogData(createGeolocItem({}, priceGeoloc));
      setOpenDialog(true);
      setErrorMessage("");
    };

    const handleCloseDialog = () => {
      setOpenDialog(false);
      setErrorMessage("");
      setDialogData(createGeolocItem({}, priceGeoloc));
    };

    const handleAddMarker = () => {
      const { cartItem, inputItem } = dialogData;

      if (!cartItem.positionName || !cartItem.latitude || !cartItem.longitude) {
        setErrorMessage(
          "Merci de remplir la totalité des champs avant de valider"
        );
        return;
      }

      setErrorMessage("");
      setShoppingCart((prevCart) => [
        {
          ...cartItem,
          id: guidGenerator(),
          price: priceGeoloc,
          startDate: cartItem.startDate || dayjs().format().substring(0, 10),
          numberOfDays: cartItem.numberOfDays || DEFAULT_NUMBER_OF_DAYS,
          radius: cartItem.radius || DEFAULT_RADIUS,
        },
        ...prevCart,
      ]);
      setShoppingCartInputs((prevInputs) => [
        {
          ...inputItem,
          positionInput: cartItem.positionName,
          radiusInput: cartItem.radius || DEFAULT_RADIUS,
          startDateInput: cartItem.startDate
            ? dayjs(cartItem.startDate)
            : dayjs(),
          numberOfDaysInput: cartItem.numberOfDays || DEFAULT_NUMBER_OF_DAYS,
        },
        ...prevInputs,
      ]);
      handleCloseDialog();
    };

    const handleFlyToCurrentPosition = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const { latitude, longitude } = position.coords;
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: 12,
          });
        });
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    };

    return (
      <PurchaseGeolocMapContainer>
        <Box
          className={classes.root}
          sx={{ flexDirection: displayOnly || geolocOnly ? "row" : "column" }}
        >
          <StyledGeocoderPaper
            elevation={0}
            id={mapID}
            className={classes.mapContainer}
            style={{
              marginBottom: displayOnly || geolocOnly ? "20px" : "0px",
              width:
                displayOnly || geolocOnly
                  ? width < 769
                    ? "calc(100% - 20px)"
                    : "100%"
                  : "100%",
            }}
          >
            <Box
              sx={{
                position: "relative",
                top: "245px",
                left: "8px",
                zIndex: 1000,
              }}
            >
              <Tooltip
                placement="right"
                title={
                  isSatellite
                    ? t("Switch to map view")
                    : t("Switch to satellite view")
                }
              >
                <IconButton
                  size="large"
                  onClick={handleToggleMapStyle}
                  sx={{
                    backgroundColor: "#D3AF5F",
                    color: "#000000",
                    "&:hover": {
                      backgroundColor: "#D3AF5F",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, .8)",
                    },
                  }}
                >
                  {isSatellite ? <MapIcon /> : <SatelliteAltIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </StyledGeocoderPaper>

          {width < 769 && (
            <Box
              sx={{
                position: "absolute",
                top: geolocOnly ? "65px" : displayOnly ? "10px" : "485px",
                right: displayOnly || geolocOnly ? "20px" : "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Button
                variant="contained"
                onClick={handleZoomIn}
                sx={{
                  marginBottom: "5px",
                  maxWidth: "40px",
                  maxHeight: "40px",
                  minWidth: "40px",
                  minHeight: "40px",
                  fontSize: "25px",
                }}
              >
                +
              </Button>
              <Button
                variant="contained"
                onClick={handleZoomOut}
                sx={{
                  maxWidth: "40px",
                  maxHeight: "40px",
                  minWidth: "40px",
                  minHeight: "40px",
                  fontSize: "25px",
                }}
              >
                -
              </Button>
            </Box>
          )}
          {displayOnly && (
            <PurchaseGeolocMapList
              purchasedGeolocs={purchasedGeolocs}
              showAll={showAll}
              setShowAll={setShowAll}
              handlePositionFocus={handlePositionFocus}
              width={width}
            />
          )}
          {geolocOnly && (
            <GeolocMapList
              geolocs={geolocs}
              showAll={showAll}
              setShowAll={setShowAll}
              handlePositionFocus={handlePositionFocus}
              width={width}
              save={save}
            />
          )}
          {!displayOnly && !geolocOnly && (
            <>
              <Paper elevation={3} className={classes.shoppingCart}>
                <Box
                  className={classes.shoppingCartTitleContainer}
                  sx={{
                    width: width < 769 ? "100%" : "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: width < 769 ? 1 : "auto",
                    }}
                  >
                    <Typography
                      variant="h5"
                      align="center"
                      className={classes.shoppingCartTitle}
                    >
                      {t("purchaseGeoloc.cart")}
                    </Typography>
                    <ShoppingCartIcon />
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent:
                        width < 769 ? "space-between" : "space-around",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: width < 769 ? "70%" : "50%",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleAddCurrentPosition}
                        disabled={isUserPositionInCart()}
                      >
                        {t("myExpariences.geolocMapList.addCurrentPosition")}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        flex: width < 769 ? 1 : "auto",
                      }}
                    >
                      <IconButton
                        color="primary"
                        onClick={handleFlyToCurrentPosition}
                        sx={{ marginRight: "10px" }}
                      >
                        <MyLocationIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={handleOpenDialog}>
                        <AddLocationAltIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
                <Box
                  className={classes.positionCardContainer}
                  sx={{
                    overflowY: shoppingCart.length > 2 ? "scroll" : "hidden",
                  }}
                >
                  {shoppingCart.length === 0 && (
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: "center",
                        marginTop: "10px",
                        marginBottom: "15px",
                      }}
                    >
                      {t("purchaseGeoloc.emptyCart")}
                    </Typography>
                  )}
                  {shoppingCart.length > 0 && !alreadyPurchased && (
                    <Paper elevation={3} className={classes.positionCard}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="h6">
                          {t("purchaseGeoloc.initializationFees")}
                        </Typography>
                        <Typography variant="h5" sx={{ marginLeft: "20px" }}>
                          300€
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                  {shoppingCart.map((geoloc, index) => {
                    return (
                      <Paper
                        elevation={3}
                        key={`geoloc-n-${index}`}
                        className={classes.positionCard}
                      >
                        <Box sx={{ display: "flex" }}>
                          <Box className={classes.positionCardInfos}>
                            <TextField
                              className={classes.positionCardInfosInput}
                              variant="outlined"
                              margin="normal"
                              label={t("positions.input")}
                              value={shoppingCartInputs[index].positionInput}
                              onChange={(e) =>
                                handleCartInfoModification(
                                  e,
                                  index,
                                  "positionInput"
                                )
                              }
                              id={`geoloc-title`}
                              name={`geoloc-title`}
                            />
                            <Typography
                              variant="body1"
                              sx={{ marginRight: "10px" }}
                            >
                              {t("positions.inputRadius")}
                            </Typography>
                            <Slider
                              value={shoppingCartInputs[index].radiusInput}
                              onChange={(e, newValue) =>
                                handleCartInfoModification(
                                  newValue,
                                  index,
                                  "radiusInput"
                                )
                              }
                              min={50}
                              max={1000}
                              step={50}
                              valueLabelDisplay="on"
                              marks
                              // sx={{ width: "75%" }}
                            />
                          </Box>
                          <Box
                            sx={{
                              width: "40%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box className={classes.positionCardActions}>
                              <Tooltip
                                title={t("purchaseGeoloc.goToThisLocation")}
                              >
                                <IconButton
                                  aria-label={t(
                                    "purchaseGeoloc.goToThisLocation"
                                  )}
                                  onClick={() => handlePositionFocus(index)}
                                >
                                  <MyLocationIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("purchaseGeoloc.delete")}>
                                <IconButton
                                  aria-label={t("purchaseGeoloc.delete")}
                                  onClick={() =>
                                    handlePositionDeletion(geoloc.id, index)
                                  }
                                  sx={{
                                    marginLeft: "10px",
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box>
                              <Typography
                                variant={width < 769 ? "body1" : "h6"}
                                sx={{
                                  textAlign: "right",
                                }}
                              >
                                {t("purchaseGeoloc.dailyPrice")}
                                {(() => {
                                  const radius =
                                    shoppingCartInputs[index]?.radiusInput ||
                                    DEFAULT_RADIUS;
                                  let dailyPrice = geoloc.price;

                                  if (radius > DEFAULT_RADIUS) {
                                    const additionalRadius =
                                      radius - DEFAULT_RADIUS;
                                    const numberOf50mIncrements = Math.floor(
                                      additionalRadius / 50
                                    );
                                    dailyPrice =
                                      geoloc.price +
                                      geoloc.price *
                                        0.1 *
                                        numberOf50mIncrements;
                                  }

                                  return dailyPrice.toFixed(2);
                                })()}
                                €
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            marginTop: "13px",
                            marginBottom: "15px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              sx={{ width: "45%" }}
                              format="DD/MM/YYYY"
                              disablePast
                              label={t("purchaseGeoloc.startDate")}
                              slots={{
                                actionBar: ActionsPicker,
                              }}
                              slotProps={{
                                actionBar: {
                                  actions: ["clear", "cancel", "accept"],
                                },
                              }}
                              onChange={(e) =>
                                handleCartInfoModification(
                                  e ? dayjs(e).format("YYYY-MM-DD") : null,
                                  index,
                                  "startDateInput"
                                )
                              }
                              value={
                                shoppingCart[index].startDate
                                  ? dayjs(shoppingCart[index].startDate)
                                  : dayjs()
                              }
                            />
                          </LocalizationProvider>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            label={t("purchaseGeoloc.numberOfDays")}
                            value={shoppingCartInputs[index].numberOfDaysInput}
                            type="number"
                            id={`number-of-days`}
                            name={`number-of-days`}
                            onChange={(e) =>
                              handleCartInfoModification(
                                e,
                                index,
                                "numberOfDaysInput"
                              )
                            }
                            sx={{
                              width: "40%",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{ textAlign: "right", marginBottom: "10px" }}
                        >
                          {t("purchaseGeoloc.totalPrice")}
                          {(() => {
                            const radius =
                              shoppingCartInputs[index].radiusInput;
                            let dailyPrice = geoloc.price;
                            if (radius > 500) {
                              const additionalRadius = radius - 500;
                              const numberOf50mIncrements = Math.floor(
                                additionalRadius / 50
                              );
                              dailyPrice =
                                geoloc.price +
                                geoloc.price * 0.1 * numberOf50mIncrements;
                            }
                            return (dailyPrice * geoloc.numberOfDays).toFixed(
                              2
                            );
                          })()}
                          €
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
              <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>
                  {t("myExpariences.geolocMapList.addMarker")}
                </DialogTitle>
                <DialogContent>
                  {errorMessage && (
                    <Typography color="error" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}
                  <TextField
                    autoFocus
                    margin="dense"
                    label={t("myExpariences.geolocMapList.title")}
                    fullWidth
                    variant="outlined"
                    value={dialogData.cartItem.positionName}
                    onChange={(e) =>
                      setDialogData((prev) => ({
                        ...prev,
                        cartItem: {
                          ...prev.cartItem,
                          positionName: e.target.value,
                        },
                      }))
                    }
                  />
                  <TextField
                    margin="dense"
                    label={t("Latitude")}
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={dialogData.cartItem.latitude}
                    onChange={(e) =>
                      setDialogData((prev) => ({
                        ...prev,
                        cartItem: {
                          ...prev.cartItem,
                          latitude: e.target.value,
                        },
                      }))
                    }
                  />
                  <TextField
                    margin="dense"
                    label={t("Longitude")}
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={dialogData.cartItem.longitude}
                    onChange={(e) =>
                      setDialogData((prev) => ({
                        ...prev,
                        cartItem: {
                          ...prev.cartItem,
                          longitude: e.target.value,
                        },
                      }))
                    }
                    sx={{ marginBottom: "14px" }}
                  />
                  <Box
                    sx={{ marginTop: "5px", marginLeft: "5px", width: "95%" }}
                  >
                    <Typography variant="body1" sx={{ marginRight: "10px" }}>
                      {t("positions.inputRadius")}
                    </Typography>
                    <Slider
                      value={dialogData.cartItem.radius}
                      onChange={(e, newValue) =>
                        setDialogData((prev) => ({
                          ...prev,
                          cartItem: {
                            ...prev.cartItem,
                            radius: newValue,
                          },
                        }))
                      }
                      min={50}
                      max={1000}
                      step={50}
                      valueLabelDisplay="on"
                      marks
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        disablePast
                        label={t("purchaseGeoloc.startDate")}
                        slots={{
                          actionBar: ActionsPicker,
                        }}
                        slotProps={{
                          actionBar: {
                            actions: ["clear", "cancel", "accept"],
                          },
                        }}
                        onChange={(e) =>
                          setDialogData((prev) => ({
                            ...prev,
                            cartItem: {
                              ...prev.cartItem,
                              startDate: e
                                ? dayjs(e).format("YYYY-MM-DD")
                                : null,
                            },
                          }))
                        }
                        value={
                          dialogData.cartItem.startDate
                            ? dayjs(dialogData.cartItem.startDate)
                            : dayjs()
                        }
                      />
                    </LocalizationProvider>
                    <TextField
                      sx={{ marginBottom: "14px" }}
                      variant="outlined"
                      margin="normal"
                      label={t("purchaseGeoloc.numberOfDays")}
                      value={dialogData.cartItem.numberOfDays}
                      type="number"
                      id={`number-of-days`}
                      name={`number-of-days`}
                      onChange={(e) =>
                        setDialogData((prev) => ({
                          ...prev,
                          cartItem: {
                            ...prev.cartItem,
                            numberOfDays: e.target.value,
                          },
                        }))
                      }
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="contained"
                    onClick={handleCloseDialog}
                    color="primary"
                  >
                    {t("myExpariences.geolocMapList.cancelMarker")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAddMarker}
                    color="primary"
                  >
                    {t("myExpariences.geolocMapList.addMarker")}
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
                <DialogTitle>{t("purchaseGeoloc.confirmDelete")}</DialogTitle>
                <DialogContent>
                  <Typography>
                    {t("purchaseGeoloc.confirmDeleteMessage")}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="contained"
                    onClick={handleCancelDelete}
                    color="primary"
                  >
                    {t("purchaseGeoloc.cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmDelete}
                    color="error"
                  >
                    {t("purchaseGeoloc.delete")}
                  </Button>
                </DialogActions>
              </Dialog>
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  right: "30%",
                  zIndex: 1000,
                  width: "100%",
                  height: "48px",
                  marginTop: "20px",
                  "@media (max-width: 768px)": {
                    width: "100%",
                    right: 0,
                    borderRadius: 0,
                  },
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: isTermsAccepted ? 0 : 1,
                    transition: "opacity 1s ease-in-out",
                    pointerEvents: isTermsAccepted ? "none" : "auto",
                    borderRadius: width < 769 ? 0 : "4px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isTermsAccepted}
                        onChange={(e) => setIsTermsAccepted(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography>
                        {t("purchaseGeoloc.doYouAccept")}{" "}
                        <Link
                          href="/salesconditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "#D3AF5F",
                            textDecoration: "underline",
                            "&:hover": {
                              color: "#D3AF5Fb3",
                            },
                            transition: "color 0.3s ease",
                          }}
                        >
                          {t("purchaseGeoloc.saleConditions")}
                        </Link>
                      </Typography>
                    }
                  />
                </Paper>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePayment}
                  disabled={totalAmount === 0}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    fontSize: width < 769 ? "14px" : "16px",
                    opacity: isTermsAccepted ? 1 : 0,
                    transition: "opacity 1s ease-in-out",
                    pointerEvents: isTermsAccepted ? "auto" : "none",
                    borderRadius: width < 769 ? 0 : "4px",
                  }}
                >
                  {`${t("purchaseGeoloc.checkout")} : ${totalAmount.toFixed(
                    2
                  )} €`}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </PurchaseGeolocMapContainer>
    );
  }
);

export default PurchaseGeolocMap;
